/**
 * workflowState — pure, DOM-free derivations over the Claude Agent SDK's dynamic
 * Workflow state. Shared by two consumers on opposite process boundaries:
 *   - the renderer stream decoder (live `task_*` events → session registry), and
 *   - the main process reloader (durable `<session>/workflows/wf_*.json` sidecars
 *     → the `workflows` map returned by `cowork:messages`).
 * Kept out of streamDecoder.ts because that module reaches for DOM globals
 * (`document`, `Notification`) and so can't compile under the main tsconfig.
 */
import type { WorkflowPhase, WorkflowAgent, WorkflowRun } from './sessionModel'

/** Whether a session still owns background Workflow work after its foreground
 * turn has gone idle. Kept here so every viewport uses the same status rule. */
export function hasRunningWorkflow(
  workflows: Readonly<Record<string, Pick<WorkflowRun, 'status'>>> | undefined,
): boolean {
  return Object.values(workflows ?? {}).some((workflow) => workflow.status === 'running')
}

/** Map an SDK task status (task_notification.status | task_updated.patch.status
 *  | the persisted `workflows/wf_*.json` `status` read on reload) to a terminal
 *  WorkflowRun status, or null when it is not terminal (running / pending /
 *  paused). SDK terminal set: 'completed' | 'failed' | 'stopped', plus
 *  task_updated's 'killed' (folded into 'stopped'). There is no 'error'. */
export function terminalWorkflowStatus(raw: string | undefined): 'completed' | 'failed' | 'stopped' | null {
  switch (raw) {
    case 'completed': return 'completed'
    case 'failed': return 'failed'
    case 'stopped':
    case 'killed': return 'stopped'
    default: return null
  }
}

/**
 * Parse a `workflow_progress` snapshot ARRAY into phases + agents. Shared by two
 * sources of the SAME shape: the live `task_progress.workflow_progress` event
 * (undeclared in the SDK d.ts), and the durable `<session>/workflows/wf_*.json`
 * file's `workflowProgress` field (read in main on reload to restore per-agent
 * detail — the SDK doesn't persist the live snapshots to the conversation
 * transcript, but it does write this sidecar next to the transcript). Returns
 * null on shape mismatch so callers degrade gracefully instead of throwing.
 */
export function parseWorkflowProgressArray(wp: unknown): { phases: WorkflowPhase[]; agents: WorkflowAgent[] } | null {
  if (!Array.isArray(wp)) return null
  const phases: WorkflowPhase[] = []
  const agents: WorkflowAgent[] = []
  for (const x of wp as any[]) {
    if (x?.type === 'workflow_phase') {
      phases.push({ index: x.index, title: x.title })
    } else if (x?.type === 'workflow_agent') {
      agents.push({
        index: x.index, label: x.label, phaseIndex: x.phaseIndex, phaseTitle: x.phaseTitle,
        agentId: x.agentId, model: x.model, fallbackModel: x.fallbackModel, isolation: x.isolation,
        state: x.state, cached: x.cached, error: x.error,
        queuedAt: x.queuedAt, startedAt: x.startedAt,
        tokens: x.tokens, toolCalls: x.toolCalls, durationMs: x.durationMs,
        promptPreview: x.promptPreview, resultPreview: x.resultPreview, attempt: x.attempt,
      })
    }
  }
  return { phases, agents }
}
